import { useState } from 'react';
import { tx } from '../../i18n';
import { num } from '../../lib/values';
import type { AreaBounds, AreaPoint, AreaSchool } from '../../types/api';

interface AreaMapProps {
  bounds: AreaBounds;
  points: AreaPoint[];
  schools: AreaSchool[];
  /** The walking radius the filters asked for, drawn around each matching school. */
  schoolMetres: number;
  /** False when no school was asked about, in which case no rings are drawn and none are implied. */
  requireSchool?: boolean;
  /** Which schools the filters count; only these get a ring and a label. */
  isWanted: (school: AreaSchool) => boolean;
  /** The postcode the list is hovering, so the two views point at the same thing. */
  highlighted: string | null;
  onHighlight: (postcode: string | null) => void;
}

/** Metres in one degree of latitude; the projection below works in degrees throughout. */
const METRES_PER_DEGREE = 111_320;

/**
 * The band buckets, best first, as one sequential teal ramp.
 *
 * <p>One hue, dark to light, because band is an ordered magnitude and not a set of categories -
 * a rainbow here would invite the reader to see seven unrelated kinds of postcode. Four buckets
 * rather than seven steps: adjacent steps of a seven-step ramp are not separable at the size of
 * a dot, and the exact band is one hover or one list row away.
 */
const BUCKETS = [
  { key: 'ab', letters: 'AB', fill: '#0b6254' },
  { key: 'c', letters: 'C', fill: '#168f7a' },
  { key: 'd', letters: 'D', fill: '#8fd3c6' },
  { key: 'efg', letters: 'EFG', fill: '#e9f6f3' },
] as const;

function bucketOf(band: string | null): (typeof BUCKETS)[number] | null {
  if (!band) {
    return null;
  }
  const letter = band.trim().toUpperCase().charAt(0);
  return BUCKETS.find((bucket) => bucket.letters.includes(letter)) ?? null;
}

function bucketLabel(letters: string): string {
  switch (letters) {
    case 'AB':
      return tx('Band A–B', 'A–B bandı', 'Klasa A–B', 'Classe A–B', 'Letra A–B');
    case 'C':
      return tx('Band C', 'C bandı', 'Klasa C', 'Classe C', 'Letra C');
    case 'D':
      return tx('Band D', 'D bandı', 'Klasa D', 'Classe D', 'Letra D');
    default:
      return tx('Band E–G', 'E–G bandı', 'Klasa E–G', 'Classe E–G', 'Letra E–G');
  }
}

/**
 * An outward code drawn from measured points, and from nothing else.
 *
 * <p>Every mark here stands for something that was read: a dot is one unit postcode's certificates
 * at that postcode's centroid, a square is a school at its own published coordinates. There is no
 * smoothing and no shaded catchment, because the register publishes neither - a heat haze between
 * two measured postcodes would be inventing a value for the ground in between, and a reader
 * cannot tell an invented colour from a measured one. A postcode that was never crawled is simply
 * not here.
 *
 * <p>The picture is not the only way to read this: it carries a summary as its accessible name and
 * the list beside it holds every number, which is also where the keyboard goes. Hovering either
 * one highlights the other.
 */
export default function AreaMap({
  bounds,
  points,
  schools,
  schoolMetres,
  requireSchool = true,
  isWanted,
  highlighted,
  onHighlight,
}: AreaMapProps) {
  const [hover, setHover] = useState<{ x: number; y: number; point: AreaPoint } | null>(null);

  const drawable = points.filter((point) => num(point.latitude) !== null && num(point.longitude) !== null);
  if (drawable.length === 0) {
    return null;
  }

  const midLat = (bounds.north + bounds.south) / 2;
  const squeeze = Math.max(0.1, Math.cos((midLat * Math.PI) / 180));
  // A margin in degrees, so a dot on the edge is not half outside the frame.
  const pad = Math.max((bounds.north - bounds.south) * 0.08, schoolMetres / METRES_PER_DEGREE);

  const north = bounds.north + pad;
  const south = bounds.south - pad;
  const west = bounds.west - pad / squeeze;
  const east = bounds.east + pad / squeeze;

  // Degrees of latitude in both directions, so one unit is the same distance on each axis and a
  // walking radius drawn as a circle really is a circle.
  const spanX = (east - west) * squeeze;
  const spanY = north - south;
  const width = 1000;
  const height = Math.max(280, Math.round((spanY / spanX) * width));

  const x = (lon: number) => ((lon - west) * squeeze * width) / spanX;
  const y = (lat: number) => ((north - lat) * height) / spanY;
  const radius = (metres: number) => (metres / METRES_PER_DEGREE / spanY) * height;

  const wanted = schools.filter(isWanted);
  const matching = drawable.filter((point) => point.matches).length;

  const summary = tx(
    `Map of ${drawable.length} postcodes in this area, ${matching} of which match the filters, with ${wanted.length} schools marked. Every number is in the list below the map.`,
    `Bu alandaki ${drawable.length} posta kodunun haritası; ${matching} tanesi filtrelere uyuyor, ${wanted.length} okul işaretli. Tüm sayılar haritanın altındaki listede.`,
    `Mapa ${drawable.length} kodów pocztowych w tym obszarze, z czego ${matching} pasuje do filtrów, z ${wanted.length} zaznaczonymi szkołami. Wszystkie liczby są na liście pod mapą.`,
    `Mapa de ${drawable.length} códigos postais nesta área, ${matching} dos quais correspondem aos filtros, com ${wanted.length} escolas assinaladas. Todos os números estão na lista abaixo.`,
    `Mapa de ${drawable.length} códigos postales de esta zona, ${matching} de los cuales cumplen los filtros, con ${wanted.length} colegios marcados. Todas las cifras están en la lista de abajo.`,
  );

  return (
    <figure className="area-map">
      <div className="area-map__frame">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="area-map__svg"
          role="img"
          aria-label={summary}
          onMouseLeave={() => {
            setHover(null);
            onHighlight(null);
          }}
        >
          {/* The walking radius, dashed because it is a straight line distance and not a route.
              Nothing is drawn when no school was asked about: a circle on a map is read as a
              condition, and inventing one would be answering a question nobody put. */}
          <g className="area-map__rings">
            {(requireSchool ? wanted : []).map((school) => (
              <circle
                key={`ring-${school.urn}`}
                cx={x(school.longitude)}
                cy={y(school.latitude)}
                r={radius(schoolMetres)}
              />
            ))}
          </g>

          <g className="area-map__points">
            {drawable.map((point) => {
              const bucket = bucketOf(point.medianBand);
              const isHighlighted = highlighted === point.postcode;
              const classNames = [
                'area-point',
                point.matches ? 'area-point--match' : 'area-point--other',
                point.certificates === 0 ? 'area-point--empty' : '',
                isHighlighted ? 'is-highlighted' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <circle
                  key={point.postcode}
                  className={classNames}
                  cx={x(point.longitude)}
                  cy={y(point.latitude)}
                  r={point.matches ? 10 : 5.5}
                  fill={point.certificates === 0 ? '#ffffff' : bucket?.fill ?? '#ffffff'}
                  onMouseEnter={() => {
                    setHover({ x: x(point.longitude), y: y(point.latitude), point });
                    onHighlight(point.postcode);
                  }}
                >
                  {/* Native tooltip and accessible name for the mark itself. */}
                  <title>
                    {point.postcode}
                    {point.certificates === 0
                      ? ` — ${tx('no certificates', 'sertifika yok', 'brak świadectw', 'sem certificados', 'sin certificados')}`
                      : ` — ${point.certificates} ${tx('certificates', 'sertifika', 'świadectw', 'certificados', 'certificados')}`}
                  </title>
                </circle>
              );
            })}
          </g>

          {/* Schools last, so they sit above the dots rather than under them. */}
          <g className="area-map__schools">
            {schools.map((school) => {
              const keep = isWanted(school);
              return (
                <g key={school.urn} className={keep ? 'area-school area-school--wanted' : 'area-school'}>
                  <rect
                    x={x(school.longitude) - 8}
                    y={y(school.latitude) - 8}
                    width={16}
                    height={16}
                    rx={4}
                  />
                  <title>
                    {school.name}
                    {school.judgement ? ` — ${school.judgement}` : ''}
                  </title>
                </g>
              );
            })}
          </g>
        </svg>

        {hover ? (
          <div
            className={
              hover.y / height < 0.25 ? 'area-map__tip area-map__tip--below' : 'area-map__tip'
            }
            style={{
              // Clamped, or a dot near an edge puts its own tooltip off the side of the frame -
              // and a tooltip flips below when the dot is too near the top for one above it.
              left: `${Math.min(86, Math.max(14, (hover.x / width) * 100))}%`,
              top: `${(hover.y / height) * 100}%`,
            }}
          >
            <p className="area-map__tip-title">{hover.point.postcode}</p>
            <p className="small">
              {hover.point.certificates === 0
                ? tx(
                    'No certificates lodged here',
                    'Burada kayıtlı sertifika yok',
                    'Brak świadectw pod tym adresem',
                    'Sem certificados registados aqui',
                    'Sin certificados registrados aquí',
                  )
                : tx(
                    `${hover.point.certificates} certificates, median band ${hover.point.medianBand ?? '—'}`,
                    `${hover.point.certificates} sertifika, medyan band ${hover.point.medianBand ?? '—'}`,
                    `${hover.point.certificates} świadectw, mediana klasy ${hover.point.medianBand ?? '—'}`,
                    `${hover.point.certificates} certificados, classe mediana ${hover.point.medianBand ?? '—'}`,
                    `${hover.point.certificates} certificados, letra mediana ${hover.point.medianBand ?? '—'}`,
                  )}
            </p>
            {hover.point.school && hover.point.school.distanceMetres !== null ? (
              <p className="small muted">
                {hover.point.school.name} · {hover.point.school.distanceMetres} m
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <figcaption className="area-map__legend">
        <span className="area-legend__group">
          {BUCKETS.map((bucket) => (
            <span key={bucket.key} className="area-legend__item">
              <span className="area-legend__dot" style={{ background: bucket.fill }} aria-hidden="true" />
              {bucketLabel(bucket.letters)}
            </span>
          ))}
          <span className="area-legend__item">
            <span className="area-legend__dot area-legend__dot--empty" aria-hidden="true" />
            {tx('No certificates', 'Sertifika yok', 'Brak świadectw', 'Sem certificados', 'Sin certificados')}
          </span>
        </span>

        <span className="area-legend__group">
          <span className="area-legend__item">
            <span className="area-legend__dot area-legend__dot--match" aria-hidden="true" />
            {tx('Matches your filters', 'Filtrelerinize uyuyor', 'Pasuje do filtrów', 'Corresponde aos filtros', 'Cumple tus filtros')}
          </span>
          <span className="area-legend__item">
            <span className="area-legend__school" aria-hidden="true" />
            {tx('School counted', 'Sayılan okul', 'Liczona szkoła', 'Escola contada', 'Colegio contado')}
          </span>
          <span className="area-legend__item">
            <span className="area-legend__ring" aria-hidden="true" />
            {tx(
              `${schoolMetres} m straight line, not a walking route`,
              `${schoolMetres} m kuş uçuşu, yürüme rotası değil`,
              `${schoolMetres} m w linii prostej, nie trasa pieszo`,
              `${schoolMetres} m em linha reta, não um percurso a pé`,
              `${schoolMetres} m en línea recta, no una ruta a pie`,
            )}
          </span>
        </span>

        <span className="small muted">
          {tx(
            'Each dot is a postcode, drawn at its centroid — not a building. Nothing is shaded between them.',
            'Her nokta bir posta kodudur ve merkez noktasında çizilir — bina değildir. Aralar boyanmaz.',
            'Każda kropka to kod pocztowy narysowany w jego centroidzie — nie budynek. Przestrzeń między nimi nie jest cieniowana.',
            'Cada ponto é um código postal, desenhado no seu centroide — não um edifício. Nada é sombreado entre eles.',
            'Cada punto es un código postal, dibujado en su centroide, no un edificio. Nada se sombrea entre ellos.',
          )}
        </span>
      </figcaption>
    </figure>
  );
}
